// TODO: Add Paint Reveal to the image https://blog.olivierlarose.com/demos/paint-reveal
"use client"

import { Heading } from "@chakra-ui/react"
import { Text } from "@chakra-ui/react"
import { Box } from "@chakra-ui/react/box"
import { Button } from "@chakra-ui/react/button"
import { HStack } from "@chakra-ui/react/stack"
import { Stack } from "@chakra-ui/react/stack"
import { VStack } from "@chakra-ui/react/stack"
import parse from "html-react-parser"
import dynamic from "next/dynamic"
import { memo, useMemo } from "react"

import ButtonLink from "@/components/ButtonLink/ButtonLink"
import Signature from "@/components/Illustrations/Signature"
import NextLink from "@/components/RouteTransitionLink"
import siteConfig from "@/config/siteConfig"
import { About } from "@/types/getAbout"
import {
  getSafeHtmlParserOptions,
  isParserElementNode,
} from "@/utils/getSafeHtmlParserOptions"
import { FileBadgeIcon, SendIcon } from "@/components/icons"

const HoverGooeyEffect = dynamic(
  () =>
    import("@/components/HoverGooeyEffect").then((mod) => mod.HoverGooeyEffect),
  { ssr: false }
)

interface AboutMeProps {
  webpage_config: About["webpage_config"]
}

const AboutMe: React.FC<AboutMeProps> = memo(({ webpage_config }) => {
  const replacedAboutText = useMemo(
    () =>
      webpage_config.about_description !== undefined
        ? parse(
            webpage_config.about_description,
            getSafeHtmlParserOptions((domNode) => {
              if (
                isParserElementNode(domNode) &&
                domNode.attribs?.id === "replace"
              ) {
                return (
                  <b className="chakra-text css-0 replaced">
                    {siteConfig.yearsDesign}+
                  </b>
                )
              }
            })
          )
        : "",
    [webpage_config.about_description]
  )

  const portraitImage1 =
    process.env.DIRECTUS_ASSETS_URL + webpage_config.portrait_image.id

  const portraitImage2 =
    process.env.DIRECTUS_ASSETS_URL + webpage_config.portrait_image_hover.id

  const cvFileUrl = webpage_config.cv_file
    ? process.env.DIRECTUS_ASSETS_URL + webpage_config.cv_file.id + "?download"
    : ""

  const socialButtons = useMemo(
    () =>
      Object.values(siteConfig.socialLinks ?? {}).map((item) => (
        <Button
          _dark={{
            _hover: { bg: "gray.900" },
            color: "primary.200",
          }}
          _hover={{
            _dark: {
              color: "primary.300",
            },
            bg: "gray.100",
            color: "social." + item.name.toLowerCase(),
            paddingX: 4,
          }}
          alignItems="center"
          asChild
          color={"social." + item.name.toLowerCase()}
          display="flex"
          key={item.name.toLowerCase()}
          px={2}
          py={0}
          rounded="md"
          transition="background-color 0.2s ease-in-out, padding 0.2s ease-in-out, color 0.2s ease-in-out"
          variant="ghost"
          w={{ base: "auto" }}
        >
          <NextLink
            href={undefined !== item.url ? item.url : "#"}
            target="_blank"
          >
            {undefined !== item.svg_icon
              ? parse(item.svg_icon, getSafeHtmlParserOptions())
              : ""}
          </NextLink>
        </Button>
      )),
    [siteConfig.socialLinks]
  )

  return (
    <VStack
      alignContent="stretch"
      alignItems="stretch"
      as="section"
      bg={"bg.paper"}
      borderColor="border"
      borderRadius={"2xl"}
      borderWidth="1px"
      className="about-me"
      flexDirection={{
        md: "row",
        mdDown: "column",
      }}
      flexWrap="nowrap"
      gap={[6, 8, 10, 10]}
      justifyContent="space-between"
      mb={6}
      overflow="visible"
      paddingBottom={[8, 10, 10, 10]}
      paddingLeft={[8, 10, 12, 12]}
      paddingRight={[8, 10, 12, 12]}
      paddingTop={[8, 10, 10, 10]}
      position="relative"
      zIndex={1}
    >
      <VStack
        alignItems="stretch"
        flexGrow="1"
        flexShrink="0"
        position="relative"
      >
        <HoverGooeyEffect image1={portraitImage1} image2={portraitImage2} />
      </VStack>
      <VStack
        alignContent="stretch"
        alignItems="flex-start"
        className="about-me-content"
        gap={3}
        justifyContent="flex-start"
        width={["100%", "100%", "100%", "100%"]}
      >
        <VStack alignItems={"flex-start"}>
          <Heading as="h1" css={{ marginBottom: 0 }} lineClamp={1} size="2xl">
            Gil Alvaro
          </Heading>
          <Text
            color="light"
            css={{
              lineHeight: "1.5",
              marginTop: "0.2rem",
            }}
            fontSize="xl"
          >
            UX Designer based in {webpage_config.location}.
          </Text>

          <Box>{replacedAboutText}</Box>
          <Stack
            direction={{ base: "row" }}
            flexWrap="wrap"
            gap="4"
            mb={2}
            mt={4}
          >
            <ButtonLink
              data-umami-event={"contact-me"}
              href="/contact"
              label="Contact Me"
              leadingIcon={<SendIcon />}
            />
            <ButtonLink
              data-umami-event={"download-cv"}
              href={cvFileUrl}
              label="Download CV"
              leadingIcon={<FileBadgeIcon />}
              variant="subtle"
            />
          </Stack>
        </VStack>
        <HStack
          alignItems={{ base: "flex-start", xl: "stretch" }}
          flexDirection={{ base: "column", xl: "row" }}
          gap={4}
          height={{ base: "auto", xl: "100%" }}
          justifyContent="space-between"
          w="full"
        >
          <Box py={4} w={["180px", "200px", "210px", "240px"]}>
            <Signature width={"100%"} />
          </Box>

          <HStack
            alignItems={{ base: "center", xl: "flex-end" }}
            css={{
              "& svg": {
                height: "1.2rem",
                width: "1.2rem",
              },
            }}
            flexBasis={"1"}
            flexGrow={1}
            flexShrink={0}
            gap={{ base: 6, md: 4 }}
            justifyContent={{ base: "space-between", xl: "flex-end" }}
            w={{ base: "100%", xl: "auto" }}
          >
            {socialButtons}
          </HStack>
        </HStack>
      </VStack>
    </VStack>
  )
})

AboutMe.displayName = "AboutMe"

export default AboutMe
